import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, PropsWithChildren, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { faro } from '@grafana/faro-web-sdk';
import useAnalytics from './useAnalytics';
import config from '../config';
import { useNavigate } from 'react-router-dom';

type Auth = {
  bearerToken: string | undefined;
  isAuthenticated: boolean;
  user: unknown;
  permissions: string[];
  logout: () => void;
};
const AuthContext = React.createContext<Auth>({
  bearerToken: undefined,
  isAuthenticated: false,
  user: null,
  permissions: [],
  logout: () => undefined,
});

// This serves as a shared state to prevent
// multiple posthog identifies when the hook gets
// mounted across multiple components in the same page
let setIdentifyCalled = false;

export const HardCodedAuthProvider: React.FC<
  PropsWithChildren<{ bearerToken: string }>
> = ({ bearerToken, children }) => {
  return (
    <AuthContext.Provider
      value={{
        bearerToken,
        isAuthenticated: true,
        user: null,
        permissions: [],
        logout: () => undefined,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const { bearerToken } = React.useContext(AuthContext);
  const prevIsAuthenticated = React.useRef(false);

  const handleLogout = async () => {
    if (window.plugSDK && typeof window.plugSDK.shutdown === 'function') {
      try {
        await window.plugSDK.shutdown();
      } catch (error) {
        console.warn('Error shutting down plugSDK:', error);
      }
    }
    analytics.reset();
    logout({
      openUrl: (url) => {
        window.location.replace(url);
      },
    });
  };

  const {
    isLoading,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
    isAuthenticated,
    user,
    error,
  } = useAuth0();

  const getToken = useCallback(
    (forceRefresh: boolean = false) => {
      return getAccessTokenSilently({
        cacheMode: forceRefresh ? 'off' : 'on',
      });
    },
    [getAccessTokenSilently],
  );

  const { data: token, isError } = useQuery({
    queryKey: ['bearerToken'],
    queryFn: () => getToken(),
    enabled: !bearerToken && isAuthenticated && !isLoading,
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      handleLogout();
    }
  }, [isError]);

  const analytics = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      return;
    } else if (error) {
      if (error.message === 'invalid_login_attempt') {
        console.error('login attempt by unregistered user from web');
        navigate('/unauthorized');
      } else if (error.message === 'social_login_not_allowed') {
        // social_login_not_allowed error is thrown by our custom hook in auth0
        console.error('social login not allowed');
        navigate('/sso-required');
      } else {
        console.error(`something went wrong with web login ${error.message}`);
        const errorMessage = encodeURIComponent(error.message);
        navigate(`/login-failed?error=${errorMessage}`);
      }
    } else if (!isAuthenticated) {
      const urlParams = new URLSearchParams(window.location.search);
      const hudToken = urlParams.get('hudtoken');
      loginWithRedirect({
        authorizationParams: {
          hudtoken: hudToken,
        },
        appState: {
          returnTo: window.location.href,
        },
        openUrl: (url) => {
          window.location.replace(url);
        },
      });
    } else {
      if (user?.sub) {
        // make sure we set identity only once
        if (!setIdentifyCalled) {
          analytics.identify(user.sub, {
            email: user.email,
            full_name: user.name,
            first_name: user.given_name,
            last_name: user.family_name,
          });
          setIdentifyCalled = true;
        }
        if (token) {
          const decodedToken = JSON.parse(atob(token.split('.')[1]));
          const accountId = decodedToken[config.AUTH0.AUDIENCE + 'accountId'];
          const isFirstLogin =
            decodedToken[config.AUTH0.AUDIENCE + 'firstLogin'];
          if (isFirstLogin) {
            void getToken(true);
          }
          analytics.setIdentityProperties({
            account_id: accountId,
          });
          faro.api.setUser({
            id: user.sub,
            email: user.email,
            username: user.nickname,
            attributes: {
              accountId,
            },
          });
        } else {
          faro.api.setUser({
            id: user.sub,
            email: user.email,
            username: user.nickname,
          });
        }
      }
    }
  }, [
    analytics,
    isLoading,
    isAuthenticated,
    user,
    error,
    loginWithRedirect,
    getToken,
    token,
    navigate,
  ]);

  useEffect(() => {
    // Reset setIdentifyCalled when switching from logged out to logged in
    if (!prevIsAuthenticated.current && isAuthenticated) {
      setIdentifyCalled = false;
    }
    prevIsAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Extract permissions from the token if available
  const permissions = React.useMemo(() => {
    if (!token) return [] as string[];
    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      return (decodedToken.permissions || []) as string[];
    } catch (error) {
      console.error('Error decoding permissions from token:', error);
      return [] as string[];
    }
  }, [token]);

  return {
    token: token ?? bearerToken ?? undefined,
    isLoading: bearerToken ? false : isLoading,
    isAuthenticated,
    user,
    permissions,
    logout: handleLogout,
  };
};
